import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';

const BranchDropdownOptionsManager = () => {
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('productOfInterest');
  const [editingOption, setEditingOption] = useState(null);
  const [newOption, setNewOption] = useState({ label: '', value: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [branchInfo, setBranchInfo] = useState(null);

  const categories = [
    { id: 'productOfInterest', label: 'Product of Interest', icon: '🎯' },
    { id: 'sector', label: 'Sector', icon: '🏭' },
    { id: 'sourceOfLead', label: 'Source of Lead', icon: '📍' },
    { id: 'productCategory', label: 'Product Category', icon: '📦' },
    { id: 'status', label: 'Status', icon: '📊' },
    { id: 'priority', label: 'Priority', icon: '⚡' }
  ];

  useEffect(() => {
    const storedBranchInfo = localStorage.getItem('branchInfo');
    if (storedBranchInfo) {
      setBranchInfo(JSON.parse(storedBranchInfo));
    }
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const token = localStorage.getItem('branchToken');
      const response = await axios.get('/dropdown-options', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOptions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching options:', error);
      setLoading(false);
    }
  };

  const handleAddOption = async () => {
    if (!newOption.label || !newOption.value) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const token = localStorage.getItem('branchToken');
      await axios.post(
        '/dropdown-options',
        {
          category: activeCategory,
          label: newOption.label,
          value: newOption.value,
          branchId: branchInfo._id
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setNewOption({ label: '', value: '' });
      setShowAddForm(false);
      fetchOptions();
      alert('✅ Option added successfully!');
    } catch (error) {
      console.error('Error adding option:', error);
      alert('❌ Failed to add option: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdateOption = async (optionId) => {
    if (!editingOption.label || !editingOption.value) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const token = localStorage.getItem('branchToken');
      await axios.put(
        `/dropdown-options/${optionId}`,
        {
          label: editingOption.label,
          value: editingOption.value,
          sortOrder: editingOption.sortOrder
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setEditingOption(null);
      fetchOptions();
      alert('✅ Option updated successfully!');
    } catch (error) {
      console.error('Error updating option:', error);
      alert('❌ Failed to update option: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteOption = async (optionId) => {
    if (!window.confirm('Are you sure you want to delete this option?')) {
      return;
    }

    try {
      const token = localStorage.getItem('branchToken');
      await axios.delete(`/dropdown-options/${optionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchOptions();
      alert('✅ Option deleted successfully!');
    } catch (error) {
      console.error('Error deleting option:', error);
      alert('❌ Failed to delete option: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleInitializeDefaults = async () => {
    if (!window.confirm('This will create default options for your branch. Continue?')) {
      return;
    }

    try {
      const token = localStorage.getItem('branchToken');
      await axios.post(
        '/dropdown-options/initialize',
        { branchId: branchInfo._id },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      fetchOptions();
      alert('✅ Default options initialized successfully!');
    } catch (error) {
      console.error('Error initializing options:', error);
      alert('❌ Failed to initialize options: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const currentOptions = options[activeCategory] || [];
  const hasOptions = Object.keys(options).length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Branch Dropdown Options</h2>
            <p className="text-gray-600 mt-1">
              Manage dropdown options for your branch: <span className="font-semibold text-purple-600">{branchInfo?.name}</span>
            </p>
            <p className="text-sm text-gray-500 mt-2">
              These options are specific to your branch and won't affect other branches.
            </p>
          </div>
          {!hasOptions && (
            <button
              onClick={handleInitializeDefaults}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Initialize Default Options
            </button>
          )}
        </div>
      </div>

      {/* Category Tabs */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => {
                setActiveCategory(category.id);
                setShowAddForm(false);
                setEditingOption(null);
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeCategory === category.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="mr-2">{category.icon}</span>
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* Options List */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {categories.find(c => c.id === activeCategory)?.label} Options
          </h3>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            {showAddForm ? 'Cancel' : '+ Add New Option'}
          </button>
        </div>

        {/* Add New Option Form */}
        {showAddForm && (
          <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h4 className="font-semibold text-gray-900 mb-3">Add New Option</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Label (Display Name)
                </label>
                <input
                  type="text"
                  value={newOption.label}
                  onChange={(e) => setNewOption({ ...newOption, label: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Manufacturing"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Value (Internal ID)
                </label>
                <input
                  type="text"
                  value={newOption.value}
                  onChange={(e) => setNewOption({ ...newOption, value: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., manufacturing"
                />
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={handleAddOption}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Add Option
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewOption({ label: '', value: '' });
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Options Table */}
        {currentOptions.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg">No options found for this category</p>
            <p className="text-sm mt-2">Click "Add New Option" to create one</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Label
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sort Order
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentOptions.map((option) => (
                  <tr key={option._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingOption?._id === option._id ? (
                        <input
                          type="text"
                          value={editingOption.label}
                          onChange={(e) => setEditingOption({ ...editingOption, label: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                        />
                      ) : (
                        <span className="text-sm font-medium text-gray-900">{option.label}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingOption?._id === option._id ? (
                        <input
                          type="text"
                          value={editingOption.value}
                          onChange={(e) => setEditingOption({ ...editingOption, value: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                        />
                      ) : (
                        <span className="text-sm text-gray-600">{option.value}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingOption?._id === option._id ? (
                        <input
                          type="number"
                          value={editingOption.sortOrder}
                          onChange={(e) => setEditingOption({ ...editingOption, sortOrder: parseInt(e.target.value) })}
                          className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                        />
                      ) : (
                        <span className="text-sm text-gray-600">{option.sortOrder}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {editingOption?._id === option._id ? (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleUpdateOption(option._id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingOption(null)}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setEditingOption(option)}
                            className="text-purple-600 hover:text-purple-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteOption(option._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BranchDropdownOptionsManager;
